import React, { useState, useRef, useEffect } from "react";
import { X, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

export default function ImageZoomModal({ isOpen, onClose, imageUrl, itemName }) {
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const imgRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setScale(1);
            setPosition({ x: 0, y: 0 });
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 5));
    const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
    const handleReset = () => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    };

    const handleWheel = (e) => {
        if (!isOpen) return;
        if (e.ctrlKey) {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            setScale(prev => Math.min(Math.max(prev + delta, 0.5), 5));
        }
    };

    useEffect(() => {
        window.addEventListener('wheel', handleWheel, { passive: false });
        return () => window.removeEventListener('wheel', handleWheel);
    }, [isOpen]);

    const onMouseDown = (e) => {
        if (e.button !== 0) return; // Only left click
        setIsDragging(true);
        setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    };

    const onMouseMove = (e) => {
        if (!isDragging) return;
        setPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    };

    const onMouseUp = () => setIsDragging(false);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex flex-col items-center justify-center overflow-hidden transition-opacity duration-300">
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between text-white bg-gradient-to-b from-black/80 to-transparent z-10">
                <div className="flex flex-col">
                    <h3 className="font-semibold text-lg">{itemName || "Image Detail"}</h3>
                    <span className="text-xs text-white/60">Zoom: {Math.round(scale * 100)}%</span>
                </div>

                <div className="flex items-center gap-4">
                    {/* Zoom Controls */}
                    <div className="flex items-center gap-1 bg-white/10 backdrop-blur-md p-1 rounded-lg border border-white/20">
                        <button
                            onClick={handleZoomOut}
                            className="p-2 hover:bg-white/20 rounded transition-colors text-white/90 hover:text-white"
                            title="Zoom Out"
                        >
                            <ZoomOut size={20} />
                        </button>
                        <button
                            onClick={handleReset}
                            className="p-2 hover:bg-white/20 rounded transition-colors text-white/90 hover:text-white"
                            title="Reset View"
                        >
                            <RotateCcw size={20} />
                        </button>
                        <button
                            onClick={handleZoomIn}
                            className="p-2 hover:bg-white/20 rounded transition-colors text-white/90 hover:text-white"
                            title="Zoom In"
                        >
                            <ZoomIn size={20} />
                        </button>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 bg-white/10 hover:bg-red-500/80 rounded-full transition-all duration-200 border border-white/20 group"
                        title="Close"
                    >
                        <X size={24} className="group-hover:scale-110" />
                    </button>
                </div>
            </div>

            {/* Main Container */}
            <div
                className={`w-full h-full flex items-center justify-center ${isDragging ? "cursor-grabbing" : "cursor-grab"} select-none`}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
            >
                <div
                    style={{
                        transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                        transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                    }}
                >
                    <img
                        ref={imgRef}
                        src={imageUrl}
                        alt={itemName}
                        className="max-w-[85vw] max-h-[85vh] object-contain shadow-2xl pointer-events-none"
                        onDragStart={(e) => e.preventDefault()}
                    />
                </div>
            </div>

            {/* Footer Info */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 text-[11px] bg-black/60 px-4 py-2 rounded-full backdrop-blur-sm border border-white/5">
                Drag to pan • Ctrl + Scroll to zoom
            </div>
        </div>
    );
}
